/**
 * Dev wallets store. Browser-local keypairs intended ONLY for testing the
 * gameplay loop on surfpool / localnet / devnet. Each wallet:
 *
 *   - is generated via `Keypair.generate()` in the browser
 *   - is persisted to `localStorage` as a JSON array of secret-key bytes
 *   - can be airdropped via the RPC faucet
 *   - can sign Anchor ix directly via `buildKeypairProgram`
 *
 * **NEVER ship these to mainnet.** The store is gated behind the same DEV /
 * `?debug=1` check the rest of the panel uses, and the secret keys live in
 * plain `localStorage` so anyone with access to the browser's devtools can
 * dump them. They exist to let a single tester drive a multi-wallet KotH
 * loop without juggling Phantom profiles.
 */
import {
  AnchorProvider,
  Program,
  type Idl as AnchorIdl,
} from "@coral-xyz/anchor";
import {
  Connection,
  Keypair,
  PublicKey,
  Transaction,
  VersionedTransaction,
} from "@solana/web3.js";

class KeypairWallet {
  constructor(readonly payer: Keypair) {}
  get publicKey(): PublicKey {
    return this.payer.publicKey;
  }
  async signTransaction<T extends Transaction | VersionedTransaction>(tx: T): Promise<T> {
    if (tx instanceof VersionedTransaction) {
      tx.sign([this.payer]);
    } else {
      (tx as Transaction).partialSign(this.payer);
    }
    return tx;
  }
  async signAllTransactions<T extends Transaction | VersionedTransaction>(txs: T[]): Promise<T[]> {
    return Promise.all(txs.map((t) => this.signTransaction(t)));
  }
}
import { IDL } from "@solblox/shared";
import { create } from "zustand";

const STORAGE_KEY = "solblox.devWallets.v1";

export interface DevWalletRecord {
  /** Stable id (random string). Used as React key + selector handle. */
  id: string;
  /** Human label, editable. */
  label: string;
  /** 64-byte secret key as a number[] so it round-trips through JSON. */
  secretKey: number[];
  /** Base58 public key, cached so we don't re-derive on every render. */
  publicKey: string;
  /** Wall-clock ms of last balance refresh; 0 = never. */
  balanceTs: number;
  /** Last observed lamports balance; null = unknown. */
  balanceLamports: number | null;
}

interface DevWalletsStore {
  wallets: DevWalletRecord[];
  /** id of the currently-selected dev wallet (for "act as"). null = none. */
  activeId: string | null;

  spawn: (label?: string) => DevWalletRecord;
  remove: (id: string) => void;
  rename: (id: string, label: string) => void;
  setActive: (id: string | null) => void;
  setBalance: (id: string, lamports: number | null) => void;
  importSecret: (secret: number[] | Uint8Array, label?: string) => DevWalletRecord;
  clearAll: () => void;
}

function loadInitial(): { wallets: DevWalletRecord[]; activeId: string | null } {
  if (typeof localStorage === "undefined") return { wallets: [], activeId: null };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { wallets: [], activeId: null };
    const parsed = JSON.parse(raw) as {
      wallets?: DevWalletRecord[];
      activeId?: string | null;
    };
    const wallets = Array.isArray(parsed.wallets) ? parsed.wallets : [];
    return { wallets, activeId: parsed.activeId ?? null };
  } catch {
    return { wallets: [], activeId: null };
  }
}

function persist(state: { wallets: DevWalletRecord[]; activeId: string | null }): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ wallets: state.wallets, activeId: state.activeId }),
    );
  } catch {
    /* localStorage quota / privacy mode — silently skip */
  }
}

let labelCounter = 0;
function defaultLabel(): string {
  labelCounter = (labelCounter + 1) >>> 0;
  return `dev-${labelCounter.toString(36)}`;
}

function makeRecord(kp: Keypair, label: string): DevWalletRecord {
  return {
    id: `w${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`,
    label,
    secretKey: Array.from(kp.secretKey),
    publicKey: kp.publicKey.toBase58(),
    balanceTs: 0,
    balanceLamports: null,
  };
}

const initial = loadInitial();
// Seed labelCounter so freshly-spawned wallets pick a non-colliding suffix.
labelCounter = initial.wallets.length;

export const useDevWallets = create<DevWalletsStore>((set, get) => ({
  wallets: initial.wallets,
  activeId: initial.activeId,

  spawn: (label) => {
    const rec = makeRecord(Keypair.generate(), label?.trim() || defaultLabel());
    set((s) => {
      const next = { wallets: [...s.wallets, rec], activeId: s.activeId ?? rec.id };
      persist(next);
      return next;
    });
    return rec;
  },

  remove: (id) =>
    set((s) => {
      const wallets = s.wallets.filter((w) => w.id !== id);
      const activeId = s.activeId === id ? (wallets[0]?.id ?? null) : s.activeId;
      const next = { wallets, activeId };
      persist(next);
      return next;
    }),

  rename: (id, label) =>
    set((s) => {
      const wallets = s.wallets.map((w) => (w.id === id ? { ...w, label } : w));
      const next = { wallets, activeId: s.activeId };
      persist(next);
      return next;
    }),

  setActive: (id) =>
    set((s) => {
      const next = { wallets: s.wallets, activeId: id };
      persist(next);
      return next;
    }),

  setBalance: (id, lamports) =>
    set((s) => {
      const wallets = s.wallets.map((w) =>
        w.id === id ? { ...w, balanceLamports: lamports, balanceTs: Date.now() } : w,
      );
      // Balance pings are noisy — persist but don't churn activeId.
      const next = { wallets, activeId: s.activeId };
      persist(next);
      return next;
    }),

  importSecret: (secret, label) => {
    const bytes = secret instanceof Uint8Array ? secret : Uint8Array.from(secret);
    if (bytes.length !== 64) {
      throw new Error(`importSecret: expected 64-byte secret key (got ${bytes.length})`);
    }
    const kp = Keypair.fromSecretKey(bytes);
    // De-dupe by publicKey.
    const dup = get().wallets.find((w) => w.publicKey === kp.publicKey.toBase58());
    if (dup) return dup;
    const rec = makeRecord(kp, label?.trim() || defaultLabel());
    set((s) => {
      const next = { wallets: [...s.wallets, rec], activeId: s.activeId ?? rec.id };
      persist(next);
      return next;
    });
    return rec;
  },

  clearAll: () =>
    set(() => {
      const next = { wallets: [], activeId: null };
      persist(next);
      return next;
    }),
}));

/** Reconstruct a `Keypair` from a stored dev-wallet record. */
export function keypairFromRecord(rec: DevWalletRecord): Keypair {
  return Keypair.fromSecretKey(Uint8Array.from(rec.secretKey));
}

/** Public-key view of a record without instantiating a Keypair. */
export function publicKeyFromRecord(rec: DevWalletRecord): PublicKey {
  return new PublicKey(rec.publicKey);
}

/**
 * Build a write-capable Anchor `Program` whose signer is the given `Keypair`.
 * Use this for the "touch as <dev wallet>" buttons in the dev console so we
 * don't have to round-trip through wallet-adapter (which is single-signer).
 */
export function buildKeypairProgram(connection: Connection, kp: Keypair): Program {
  const wallet = new KeypairWallet(kp);
  const provider = new AnchorProvider(connection, wallet, {
    commitment: "confirmed",
    preflightCommitment: "confirmed",
  });
  return new Program(IDL as unknown as AnchorIdl, provider);
}
